import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Activity, RefreshCw, Wifi, WifiOff, AlertTriangle,
  CheckCircle, XCircle, Clock, Zap, ArrowDown,
  Server, Globe, TrendingUp, Loader2, ChevronDown, ChevronUp,
  Signal, AlertCircle, Info, Eye, Code, Shield, Radio,
  DollarSign, ArrowRightLeft, Star, CircleDot
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  healthy: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle, label: 'Healthy', dot: 'bg-emerald-500' },
  degraded: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: AlertTriangle, label: 'Degraded', dot: 'bg-amber-500' },
  down: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: XCircle, label: 'Down', dot: 'bg-red-500' },
};

const formatBlock = (n) => n ? n.toLocaleString() : '—';
const formatLatency = (ms) => ms ? `${Math.round(ms)}ms` : '—';

const PRIORITY_TIER_CONFIG = {
  1: { label: 'Primary', short: 'T1', full: 'Free Primary', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  2: { label: 'Backup', short: 'T2', full: 'Free Backup', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  3: { label: 'Emergency', short: 'T3', full: 'Paid Emergency', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
};

// ─── Cost Tier Badge (FREE / PAID) ──────────────────────
const CostTierBadge = ({ tier }) => {
  if (tier === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/20" data-testid="tier-paid">
        <DollarSign className="h-2.5 w-2.5" />PAID
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-testid="tier-free">
      FREE
    </span>
  );
};

// ─── Priority Tier Badge (Primary / Backup / Emergency) ─
const PriorityTierBadge = ({ tier, showShort = false }) => {
  const cfg = PRIORITY_TIER_CONFIG[tier] || PRIORITY_TIER_CONFIG[1];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}
      data-testid={`priority-tier-${tier}`} title={cfg.full}>
      {showShort ? cfg.short : cfg.label}
    </span>
  );
};

// ─── Active Node Badge ──────────────────────────────────
const ActiveBadge = () => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 animate-pulse" data-testid="active-node-badge">
    <CircleDot className="h-2.5 w-2.5" />ACTIVE
  </span>
);

// ─── Node Status Indicator (🟢/🟡/🔴) ─────────────────
const NodeStatusIndicator = ({ data, failoverData }) => {
  const networks = failoverData?.networks || [];
  const anyPaid = networks.some(n => n.using_paid);
  const anyPending = networks.some(n => n.pending_paid_activation);
  const anyBackup = data.some(n => {
    const fo = networks.find(f => f.network_id === n.network_id);
    return fo && fo.priority_tier === 2 && !fo.using_paid;
  });

  let variant = 'free';
  let title = 'Free Node Active';
  let subtitle = 'Normal Operation — All networks are using free/public RPC endpoints';
  let dotColor = 'bg-emerald-500';
  let textColor = 'text-emerald-300';
  let borderColor = 'border-emerald-500/20';
  let bgColor = 'bg-emerald-500/5';

  if (anyPaid) {
    variant = 'premium';
    title = 'Premium Node Active';
    subtitle = 'Emergency reliability mode — Premium backup is handling requests to prevent missed payments';
    dotColor = 'bg-red-500';
    textColor = 'text-red-300';
    borderColor = 'border-red-500/30';
    bgColor = 'bg-red-500/5';
  } else if (anyPending) {
    variant = 'pending';
    title = 'Backup Node Active';
    subtitle = 'System adjusting — Free nodes degraded, monitoring before premium activation';
    dotColor = 'bg-amber-500';
    textColor = 'text-amber-300';
    borderColor = 'border-amber-500/30';
    bgColor = 'bg-amber-500/5';
  } else if (anyBackup) {
    variant = 'backup';
    title = 'Backup Node Active';
    subtitle = 'System adjusting — Primary free nodes degraded, using backup free nodes';
    dotColor = 'bg-amber-500';
    textColor = 'text-amber-300';
    borderColor = 'border-amber-500/30';
    bgColor = 'bg-amber-500/5';
  }

  return (
    <Card className={`border ${borderColor} ${bgColor}`} data-testid="node-status-indicator">
      <CardContent className="py-4 flex items-center gap-4">
        <div className="relative">
          <div className={`w-4 h-4 rounded-full ${dotColor}`} />
          <div className={`absolute inset-0 w-4 h-4 rounded-full ${dotColor} animate-ping opacity-30`} />
        </div>
        <div>
          <p className={`text-sm font-bold ${textColor}`}>{title}</p>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Premium Alert Cards ────────────────────────────────
const PremiumAlertCards = ({ events, failoverData }) => {
  const networks = failoverData?.networks || [];
  const anyPaid = networks.some(n => n.using_paid);
  const anyReverting = networks.some(n => n.pending_free_reversion);

  // Find most recent activation/reversion events
  const recentActivation = events.find(e => e.type === 'paid_activated');
  const recentReversion = events.find(e => e.type === 'reversion');

  const alerts = [];

  if (anyPaid) {
    const paidNets = networks.filter(n => n.using_paid);
    alerts.push({
      key: 'activated',
      icon: AlertCircle,
      title: 'Premium Backup Activated',
      lines: [
        'All free blockchain servers were unavailable.',
        'The system switched to a premium backup to prevent missed payments.',
      ],
      color: 'red',
      network: paidNets.map(n => n.network_name).join(', '),
    });
  }

  if (anyReverting) {
    const revertNets = networks.filter(n => n.pending_free_reversion);
    alerts.push({
      key: 'reverting',
      icon: ArrowRightLeft,
      title: 'Returning to Free Servers',
      lines: [
        'Free blockchain servers are recovering.',
        'Verifying stability before deactivating premium backup.',
      ],
      color: 'cyan',
      network: revertNets.map(n => n.network_name).join(', '),
    });
  }

  // Show recent reversion success (within last 5 minutes)
  if (!anyPaid && recentReversion && !anyReverting) {
    const evtTime = new Date(recentReversion.timestamp);
    const minutesAgo = (Date.now() - evtTime.getTime()) / 60000;
    if (minutesAgo < 5) {
      alerts.push({
        key: 'reverted',
        icon: CheckCircle,
        title: 'Returned to Free Servers',
        lines: [
          'Free blockchain servers are working again.',
          'Premium backup has been deactivated to control costs.',
        ],
        color: 'emerald',
        network: recentReversion.network_name,
      });
    }
  }

  if (alerts.length === 0) return null;

  const colorMap = {
    red: { bg: 'bg-red-500/8', border: 'border-red-500/25', icon: 'text-red-400', title: 'text-red-300', text: 'text-red-400/80' },
    cyan: { bg: 'bg-cyan-500/8', border: 'border-cyan-500/25', icon: 'text-cyan-400', title: 'text-cyan-300', text: 'text-cyan-400/80' },
    emerald: { bg: 'bg-emerald-500/8', border: 'border-emerald-500/25', icon: 'text-emerald-400', title: 'text-emerald-300', text: 'text-emerald-400/80' },
  };

  return (
    <div className="space-y-3" data-testid="premium-alert-cards">
      {alerts.map(a => {
        const c = colorMap[a.color];
        const Icon = a.icon;
        return (
          <Card key={a.key} className={`border ${c.border} ${c.bg}`} data-testid={`premium-alert-${a.key}`}>
            <CardContent className="py-4 flex items-start gap-4">
              <div className="p-2 rounded-lg bg-black/20">
                <Icon className={`h-5 w-5 ${c.icon}`} />
              </div>
              <div>
                <p className={`text-sm font-bold ${c.title}`}>{a.title}</p>
                {a.lines.map((line, i) => (
                  <p key={i} className={`text-xs ${c.text} mt-0.5`}>{line}</p>
                ))}
                {a.network && <p className="text-[10px] text-muted-foreground mt-1.5">Network: {a.network}</p>}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// ─── Cost Usage Card ────────────────────────────────────
const CostUsageCard = ({ costData }) => {
  if (!costData) return null;

  const periods = [
    { label: 'Today', data: costData.today },
    { label: 'Last 7 Days', data: costData.week },
    { label: 'Last 30 Days', data: costData.month },
  ];

  return (
    <Card className="border-border bg-card" data-testid="cost-usage-card">
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="h-4 w-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-foreground">Premium Node Usage</h3>
          {costData.currently_active_paid > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/15 text-red-400 animate-pulse">
              {costData.currently_active_paid} active now
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {periods.map(p => (
            <div key={p.label} className="text-center p-3 rounded-lg bg-muted/20">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{p.label}</p>
              <p className="text-lg font-bold text-foreground">{p.data?.activations || 0}</p>
              <p className="text-[10px] text-muted-foreground">activations</p>
              <p className="text-xs font-mono text-amber-400 mt-1">{p.data?.total_minutes || 0} min</p>
              {(p.data?.estimated_requests > 0) && (
                <p className="text-[10px] text-muted-foreground mt-0.5">{p.data.estimated_requests.toLocaleString()} req</p>
              )}
              {(p.data?.estimated_cost_usd > 0) && (
                <p className="text-[10px] font-mono text-red-400/80 mt-0.5">${p.data.estimated_cost_usd}</p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Failover Event Log ─────────────────────────────────
const FailoverEventLog = ({ events, premiumLogs }) => {
  const hasEvents = events && events.length > 0;
  const hasLogs = premiumLogs && premiumLogs.length > 0;

  if (!hasEvents && !hasLogs) return null;

  const sevConfig = {
    critical: { color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle },
    warning: { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: AlertTriangle },
    success: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle },
    info: { color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Info },
  };

  return (
    <Card className="border-border bg-card" data-testid="failover-event-log">
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRightLeft className="h-4 w-4 text-indigo-400" />
          <h3 className="text-sm font-semibold text-foreground">Failover Events</h3>
          {hasEvents && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{events.length}</span>}
        </div>

        {/* Premium Usage Logs */}
        {hasLogs && (
          <div className="space-y-2 mb-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Premium Usage Log</p>
            {premiumLogs.map(log => (
              <div key={log.id} className="px-3 py-2.5 rounded-lg bg-red-500/5 border border-red-500/15" data-testid="premium-usage-log-entry">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-semibold text-red-300">
                    Premium node {log.status === 'active' ? 'activated' : 'used'}
                  </p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${log.status === 'active' ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-muted text-muted-foreground'}`}>
                    {log.status === 'active' ? 'LIVE' : 'Completed'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                  <p className="text-muted-foreground">Reason: <span className="text-foreground">{log.reason?.replace(/^PAID ACTIVATED: /, '').slice(0, 60) || 'Free nodes unreachable'}</span></p>
                  <p className="text-muted-foreground">Duration: <span className="text-foreground font-mono">{log.duration_display}</span></p>
                  <p className="text-muted-foreground">Est. requests: <span className="text-foreground font-mono">{(log.estimated_requests || 0).toLocaleString()}</span></p>
                  <p className="text-muted-foreground">Est. cost: <span className="text-amber-400 font-mono">${log.estimated_cost_usd || '0.0000'}</span></p>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {log.endpoint_label} on {log.network_name} — {log.activated_at ? new Date(log.activated_at).toLocaleString() : ''}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Event Timeline */}
        {hasEvents && (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {events.map(evt => {
              const cfg = sevConfig[evt.severity] || sevConfig.info;
              const Icon = cfg.icon;
              const time = evt.timestamp ? new Date(evt.timestamp).toLocaleString() : '';
              return (
                <div key={evt.id} className={`flex items-start gap-2.5 px-3 py-2 rounded-lg ${cfg.bg}`} data-testid={`event-${evt.type}`}>
                  <Icon className={`h-3.5 w-3.5 ${cfg.color} shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium ${cfg.color}`}>{evt.network_name}: {evt.detail}</p>
                    <p className="text-[10px] text-muted-foreground">{time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Human-Friendly Helpers ─────────────────────────────
const getBlockDisplay = (ep) => {
  if (ep.cost_tier === 'paid' && !ep.is_active_node) return '—';
  if (ep.status === 'down' || ep.has_error) return '—';
  if (ep.block_lag === 0) return formatBlock(ep.block_number);
  return `${ep.block_lag} behind`;
};

const getSpeedDisplay = (ep) => {
  if (ep.cost_tier === 'paid' && !ep.is_active_node) return '—';
  if (ep.status === 'down') return '—';
  return `${Math.round(ep.latency_ms)} ms`;
};

const getStatusDisplay = (ep) => {
  if (ep.cost_tier === 'paid' && !ep.is_active_node && ep.priority_tier === 3) return 'Standby';
  if (ep.status === 'healthy') return 'Healthy';
  if (ep.status === 'degraded') return 'Slow';
  return 'Down';
};

const getStatusColor = (ep) => {
  const display = getStatusDisplay(ep);
  if (display === 'Healthy') return { color: 'text-emerald-400', bg: 'bg-emerald-500/10', icon: CheckCircle, dot: 'bg-emerald-500' };
  if (display === 'Slow') return { color: 'text-amber-400', bg: 'bg-amber-500/10', icon: AlertTriangle, dot: 'bg-amber-500' };
  if (display === 'Standby') return { color: 'text-slate-400', bg: 'bg-slate-500/10', icon: Shield, dot: 'bg-slate-500' };
  return { color: 'text-red-400', bg: 'bg-red-500/10', icon: XCircle, dot: 'bg-red-500' };
};

const getExplanation = (ep) => {
  if (ep.cost_tier === 'paid' && !ep.is_active_node && ep.priority_tier === 3) return 'Used only if needed';
  if (ep.status === 'down') {
    if (ep.last_error?.includes('timeout') || ep.last_error?.includes('Timeout')) return 'Connection timed out';
    if (ep.last_error?.includes('HTTP')) return `Server error (${ep.last_error.slice(0, 30)})`;
    if (ep.last_error?.includes('Name or service')) return 'Server unreachable';
    return ep.last_error ? ep.last_error.slice(0, 50) : 'Connection failed';
  }
  if (ep.status === 'degraded') {
    const reasons = [];
    if (ep.block_lag > 2) reasons.push(`${ep.block_lag} blocks behind`);
    if (ep.latency_ms > 800) reasons.push('slow response');
    if (ep.error_rate > 20) reasons.push('intermittent errors');
    return reasons.length ? `Slight delay — ${reasons.join(', ')}` : 'Reduced performance';
  }
  return 'Working perfectly';
};

const getPriorityLabel = (tier) => {
  if (tier === 1) return 'Primary';
  if (tier === 2) return 'Backup';
  if (tier === 3) return 'Emergency';
  return 'Primary';
};

const getSyncText = (ep) => {
  if (ep.status === 'down' || ep.has_error) return 'Not syncing';
  if (ep.block_lag === 0) return 'Up-to-date';
  if (ep.block_lag === 1) return '1 block behind';
  return `${ep.block_lag} blocks behind`;
};

const getBestNode = (endpoints) => {
  const working = (endpoints || []).filter(e => e.status !== 'down');
  if (!working.length) return null;
  return working.reduce((best, ep) => (!best || ep.latency_ms < best.latency_ms) ? ep : best, null);
};

const getListenerMode = (endpoints) => {
  const wss = (endpoints || []).find(e => e.connection_type === 'WSS' && e.status !== 'down');
  if (wss) return 'WebSocket';
  const rpc = (endpoints || []).find(e => e.connection_type === 'HTTPS' && e.status !== 'down');
  if (rpc) return 'HTTP Polling';
  return 'Offline';
};

// ─── Summary Cards ──────────────────────────────────────
const SummaryCards = ({ data }) => {
  const allEndpoints = data.flatMap(n => n.endpoints || []);
  const healthy = allEndpoints.filter(e => e.status === 'healthy').length;
  const degraded = allEndpoints.filter(e => e.status === 'degraded').length;
  const down = allEndpoints.filter(e => e.status === 'down').length;
  const total = allEndpoints.length;

  const cards = [
    { label: 'Total Nodes', value: total, icon: Server, accent: 'text-slate-300' },
    { label: 'Healthy', value: healthy, icon: CheckCircle, accent: 'text-emerald-400' },
    { label: 'Degraded', value: degraded, icon: AlertTriangle, accent: 'text-amber-400' },
    { label: 'Down', value: down, icon: XCircle, accent: 'text-red-400' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
      {cards.map(c => (
        <Card key={c.label} className="border-border bg-card" data-testid={`summary-${c.label.toLowerCase()}`}>
          <CardContent className="py-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-muted/40`}>
              <c.icon className={`h-5 w-5 ${c.accent}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{c.value}</p>
              <p className="text-xs text-muted-foreground">{c.label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ─── Alert Banner ───────────────────────────────────────
const AlertBanner = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;
  const critical = alerts.filter(a => a.severity === 'critical');
  const warnings = alerts.filter(a => a.severity === 'warning');

  return (
    <div className="space-y-2" data-testid="alert-banners">
      {critical.map((a, i) => (
        <div key={`c-${i}`} className="flex items-start gap-3 px-4 py-3 rounded-lg bg-red-500/8 border border-red-500/20" data-testid="alert-critical">
          <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-300">{a.message}</p>
            <p className="text-xs text-red-400/70 mt-0.5">{a.action}</p>
          </div>
        </div>
      ))}
      {warnings.map((a, i) => (
        <div key={`w-${i}`} className="flex items-start gap-3 px-4 py-3 rounded-lg bg-amber-500/8 border border-amber-500/20" data-testid="alert-warning">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-300">{a.message}</p>
            <p className="text-xs text-amber-400/70 mt-0.5">{a.action}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Endpoint Row ───────────────────────────────────────
const EndpointRow = ({ ep }) => {
  const cfg = STATUS_CONFIG[ep.status] || STATUS_CONFIG.down;
  const StatusIcon = cfg.icon;

  const lagColor = ep.block_lag === 0 ? 'text-emerald-400' : ep.block_lag <= 2 ? 'text-emerald-400' : ep.block_lag <= 10 ? 'text-amber-400' : 'text-red-400';
  const latColor = ep.latency_ms < 800 ? 'text-emerald-400' : ep.latency_ms < 2000 ? 'text-amber-400' : 'text-red-400';
  const errColor = ep.error_rate === 0 ? 'text-muted-foreground' : ep.error_rate < 20 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="flex items-center gap-3 py-3 px-4 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors" data-testid={`endpoint-row-${ep.endpoint_label?.replace(/\s+/g, '-').toLowerCase()}`}>
      {/* Status dot + Type */}
      <div className="flex items-center gap-2 w-24 shrink-0">
        <div className={`w-2 h-2 rounded-full ${cfg.dot} animate-pulse`} />
        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${ep.connection_type === 'WSS' ? 'bg-purple-500/15 text-purple-400' : 'bg-blue-500/15 text-blue-400'}`}>
          {ep.connection_type}
        </span>
      </div>

      {/* Label + URL + Badges */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-sm font-medium text-foreground truncate">{ep.endpoint_label}</p>
          <PriorityTierBadge tier={ep.priority_tier || 1} showShort />
          <CostTierBadge tier={ep.cost_tier || 'free'} />
          {ep.is_active_node && <ActiveBadge />}
        </div>
        <p className="text-[11px] text-muted-foreground truncate font-mono">{ep.endpoint_url}</p>
      </div>

      {/* Block Number */}
      <div className="text-right w-28 shrink-0 hidden lg:block">
        <p className="text-sm font-mono text-foreground">{formatBlock(ep.block_number)}</p>
        <p className="text-[10px] text-muted-foreground">block</p>
      </div>

      {/* Block Lag */}
      <div className="text-right w-20 shrink-0">
        <p className={`text-sm font-mono font-medium ${lagColor}`}>{ep.block_lag}</p>
        <p className="text-[10px] text-muted-foreground">lag</p>
      </div>

      {/* Latency */}
      <div className="text-right w-20 shrink-0">
        <p className={`text-sm font-mono font-medium ${latColor}`}>{formatLatency(ep.latency_ms)}</p>
        <p className="text-[10px] text-muted-foreground">latency</p>
      </div>

      {/* Error Rate */}
      <div className="text-right w-16 shrink-0 hidden md:block">
        <p className={`text-sm font-mono ${errColor}`}>{ep.error_rate}%</p>
        <p className="text-[10px] text-muted-foreground">errors</p>
      </div>

      {/* Status Badge */}
      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color} ${cfg.border} border w-24 justify-center shrink-0`} data-testid={`status-badge-${ep.endpoint_label?.replace(/\s+/g, '-').toLowerCase()}`}>
        <StatusIcon className="h-3 w-3" />
        {cfg.label}
      </div>
    </div>
  );
};

// ─── Network Card ───────────────────────────────────────
const NetworkCard = ({ network }) => {
  const [expanded, setExpanded] = useState(true);
  const cfg = STATUS_CONFIG[network.status] || STATUS_CONFIG.down;
  const StatusIcon = cfg.icon;

  return (
    <Card className="border-border bg-card overflow-hidden" data-testid={`network-card-${network.network_id}`}>
      <div className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-muted/10 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${cfg.bg}`}>
            <Globe className={`h-5 w-5 ${cfg.color}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{network.network_name}</h3>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs text-muted-foreground">{network.total_endpoints} endpoint{network.total_endpoints !== 1 ? 's' : ''}</span>
              <span className="text-xs text-muted-foreground">Highest: <span className="font-mono text-foreground">{formatBlock(network.highest_block)}</span></span>
              <span className="text-xs text-muted-foreground">Avg latency: <span className="font-mono text-foreground">{formatLatency(network.avg_latency_ms)}</span></span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Mini status dots */}
          <div className="flex gap-1.5 items-center">
            {network.healthy_count > 0 && (
              <span className="flex items-center gap-1 text-xs text-emerald-400">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />{network.healthy_count}
              </span>
            )}
            {network.degraded_count > 0 && (
              <span className="flex items-center gap-1 text-xs text-amber-400">
                <span className="w-2 h-2 rounded-full bg-amber-500" />{network.degraded_count}
              </span>
            )}
            {network.down_count > 0 && (
              <span className="flex items-center gap-1 text-xs text-red-400">
                <span className="w-2 h-2 rounded-full bg-red-500" />{network.down_count}
              </span>
            )}
          </div>

          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color} ${cfg.border} border`}>
            <StatusIcon className="h-3.5 w-3.5" /> {cfg.label}
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-4 space-y-2 border-t border-border pt-3">
          {/* Column headers */}
          <div className="flex items-center gap-3 px-4 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            <div className="w-24 shrink-0">Type</div>
            <div className="flex-1">Endpoint</div>
            <div className="w-28 text-right shrink-0 hidden lg:block">Block</div>
            <div className="w-20 text-right shrink-0">Lag</div>
            <div className="w-20 text-right shrink-0">Latency</div>
            <div className="w-16 text-right shrink-0 hidden md:block">Errors</div>
            <div className="w-24 text-center shrink-0">Status</div>
          </div>
          {(network.endpoints || []).map((ep, i) => (
            <EndpointRow key={`${ep.endpoint_url}-${i}`} ep={ep} />
          ))}
          {(!network.endpoints || network.endpoints.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-4">No endpoints configured</p>
          )}
        </div>
      )}
    </Card>
  );
};

// ─── Simple Network Card (Non-Technical) ────────────────
const SimpleNetworkCard = ({ network, hideType = false }) => {
  const cfg = STATUS_CONFIG[network.status] || STATUS_CONFIG.down;
  const best = getBestNode(network.endpoints);
  const listenerMode = getListenerMode(network.endpoints);
  const allDown = (network.endpoints || []).every(e => e.status === 'down');

  const modeIcon = listenerMode === 'WebSocket' ? Radio : listenerMode === 'HTTP Polling' ? Globe : WifiOff;
  const modeColor = listenerMode === 'WebSocket' ? 'text-purple-400' : listenerMode === 'HTTP Polling' ? 'text-blue-400' : 'text-red-400';

  return (
    <Card className="border-border bg-card overflow-hidden" data-testid={`simple-network-${network.network_id}`}>
      {/* Network Header */}
      <div className={`px-5 py-4 border-b ${cfg.border} ${cfg.bg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${cfg.dot}`} />
            <h3 className="text-base font-semibold text-foreground">Network: {network.network_name}</h3>
            {network.using_paid && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/15 text-red-400 border border-red-500/20 animate-pulse" data-testid="using-paid-badge">
                <DollarSign className="h-2.5 w-2.5 inline" /> Premium Active
              </span>
            )}
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
            <cfg.icon className="h-3.5 w-3.5" /> {allDown ? 'All Nodes Down' : cfg.label}
          </div>
        </div>
      </div>

      {/* Best Node Info */}
      <div className="px-5 py-4 border-b border-border">
        {best ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2.5" data-testid="best-node-info">
              <CheckCircle className="h-4 w-4 text-emerald-400 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Best Node</p>
                <p className="text-sm font-medium text-foreground">{best.endpoint_label}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <TrendingUp className="h-4 w-4 text-cyan-400 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Latest Block</p>
                <p className="text-sm font-medium text-foreground font-mono">{formatBlock(network.highest_block)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Zap className="h-4 w-4 text-amber-400 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Response Speed</p>
                <p className="text-sm font-medium text-foreground">{Math.round(best.latency_ms)} ms</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              {React.createElement(modeIcon, { className: `h-4 w-4 ${modeColor} shrink-0` })}
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Listener Mode</p>
                <p className={`text-sm font-medium ${modeColor}`}>{listenerMode}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-400">
            <WifiOff className="h-4 w-4" />
            <p className="text-sm font-medium">No working nodes available</p>
          </div>
        )}
      </div>

      {/* Node Table — Clean format: Node | Type | Priority | Status | Block | Speed | Explanation */}
      <div className="px-5 py-3">
        <table className="w-full" data-testid="simple-node-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="text-left pb-2.5 font-medium">Node</th>
              {!hideType && <th className="text-left pb-2.5 font-medium">Type</th>}
              <th className="text-left pb-2.5 font-medium">Priority</th>
              <th className="text-left pb-2.5 font-medium">Status</th>
              <th className="text-left pb-2.5 font-medium">Block</th>
              <th className="text-left pb-2.5 font-medium">Speed</th>
              <th className="text-left pb-2.5 font-medium">Explanation</th>
            </tr>
          </thead>
          <tbody>
            {(network.endpoints || []).map((ep, i) => {
              const sc = getStatusColor(ep);
              const SIcon = sc.icon;
              const statusLabel = getStatusDisplay(ep);
              return (
                <tr key={`${ep.endpoint_url}-${i}`} className="border-b border-border/50 last:border-0" data-testid={`simple-row-${ep.endpoint_label?.replace(/\s+/g, '-').toLowerCase()}`}>
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">{ep.endpoint_label}</p>
                      {ep.is_active_node && <ActiveBadge />}
                    </div>
                  </td>
                  {!hideType && (
                  <td className="py-3 pr-3">
                    <CostTierBadge tier={ep.cost_tier || 'free'} />
                  </td>
                  )}
                  <td className="py-3 pr-3">
                    <PriorityTierBadge tier={ep.priority_tier || 1} />
                  </td>
                  <td className="py-3 pr-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                      <SIcon className="h-3 w-3" /> {statusLabel}
                    </span>
                  </td>
                  <td className="py-3 pr-3">
                    <span className="text-sm font-mono text-foreground">{getBlockDisplay(ep)}</span>
                  </td>
                  <td className="py-3 pr-3">
                    <span className="text-sm font-mono text-foreground">{getSpeedDisplay(ep)}</span>
                  </td>
                  <td className="py-3">
                    <span className="text-sm text-muted-foreground">{getExplanation(ep)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

// ─── Main Component ─────────────────────────────────────
const NodeHealth = () => {
  const [data, setData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [costData, setCostData] = useState(null);
  const [failoverData, setFailoverData] = useState(null);
  const [events, setEvents] = useState([]);
  const [premiumLogs, setPremiumLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [viewMode, setViewMode] = useState('simple'); // 'simple' | 'technical'
  const intervalRef = useRef(null);
  const { user } = useAuth();
  const isSupport = user?.member_role === 'support';

  const fetchData = useCallback(async (showToast = false) => {
    try {
      const [healthRes, alertRes, costRes, failoverRes, eventsRes, premiumRes] = await Promise.all([
        axios.get(`${API}/admin/node-health`),
        axios.get(`${API}/admin/node-health/alerts`),
        axios.get(`${API}/admin/node-health/cost-usage`),
        axios.get(`${API}/admin/node-health/failover-status`),
        axios.get(`${API}/admin/node-health/events`),
        axios.get(`${API}/admin/node-health/premium-usage-log`),
      ]);
      setData(healthRes.data.networks || []);
      setLastCheck(healthRes.data.last_check);
      setAlerts(alertRes.data.alerts || []);
      setCostData(costRes.data);
      setFailoverData(failoverRes.data);
      setEvents(eventsRes.data.events || []);
      setPremiumLogs(premiumRes.data.logs || []);
      if (showToast) toast.success('Health data refreshed');
    } catch (e) {
      if (showToast) toast.error('Failed to fetch health data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchData(), 30000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, fetchData]);

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const { data: result } = await axios.post(`${API}/admin/node-health/check-now`);
      toast.success(`Check complete: ${result.healthy} healthy, ${result.degraded} degraded, ${result.down} down`);
      await fetchData();
    } catch {
      toast.error('Health check failed');
    }
    setChecking(false);
  };

  const timeSince = lastCheck ? (() => {
    const diff = Math.floor((Date.now() - new Date(lastCheck).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  })() : null;

  if (loading) {
    return (
      <AdminLayout title="Node Health">
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Node Health">
      <div className="space-y-5" data-testid="node-health-page">
        {/* Header actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {timeSince && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Last check: {timeSince}
              </span>
            )}
            <button onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${autoRefresh ? 'bg-emerald-500/10 text-emerald-400' : 'bg-muted/30 text-muted-foreground'}`}
              data-testid="auto-refresh-toggle">
              <Signal className={`h-3 w-3 ${autoRefresh ? 'animate-pulse' : ''}`} />
              {autoRefresh ? 'Live' : 'Paused'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* View Mode Toggle — hidden for support role */}
            {!isSupport && (
            <div className="flex p-0.5 bg-muted/30 rounded-lg" data-testid="view-mode-toggle">
              <button onClick={() => setViewMode('simple')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'simple' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="view-simple-btn">
                <Eye className="h-3.5 w-3.5" /> Simple
              </button>
              <button onClick={() => setViewMode('technical')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'technical' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="view-technical-btn">
                <Code className="h-3.5 w-3.5" /> Technical
              </button>
            </div>
            )}
            <Button size="sm" onClick={handleCheckNow} disabled={checking} data-testid="check-now-btn">
              {checking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Check Now
            </Button>
          </div>
        </div>

        {/* Summary */}
        <SummaryCards data={data} />

        {/* Clear Status Indicator */}
        {data.length > 0 && <NodeStatusIndicator data={data} failoverData={failoverData} />}

        {/* Premium Alerts */}
        <PremiumAlertCards events={events} failoverData={failoverData} />

        {/* Cost Usage + Events Row */}
        {(costData && (costData.currently_active_paid > 0 || costData.month?.activations > 0)) || events.length > 0 || premiumLogs.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {costData && (costData.currently_active_paid > 0 || costData.month?.activations > 0) && (
              <CostUsageCard costData={costData} />
            )}
            {(events.length > 0 || premiumLogs.length > 0) && <FailoverEventLog events={events} premiumLogs={premiumLogs} />}
          </div>
        ) : null}

        {/* Alerts */}
        <AlertBanner alerts={alerts} />

        {/* Network Cards */}
        {data.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Info className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No health data available yet. A check has been triggered automatically — data will appear shortly. You can also click "Check Now" above.</p>
            </CardContent>
          </Card>
        ) : viewMode === 'simple' ? (
          <div className="space-y-4" data-testid="simple-view">
            {data.map(net => (
              <SimpleNetworkCard key={net.network_id} network={net} hideType={isSupport} />
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-testid="network-cards">
            {data.map(net => (
              <NetworkCard key={net.network_id} network={net} />
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="space-y-2 pt-2">
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="font-medium">Priority:</span>
            <span className="flex items-center gap-1"><PriorityTierBadge tier={1} /> Free Primary — used first</span>
            <span className="flex items-center gap-1"><PriorityTierBadge tier={2} /> Free Backup — when primary degrades</span>
            <span className="flex items-center gap-1"><PriorityTierBadge tier={3} /> Paid Emergency — all free nodes fail</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="font-medium">Status:</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Healthy — working perfectly</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Slow — degraded performance</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Down — not responding</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-500" /> Standby — used only if needed</span>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default NodeHealth;
